// frontend/src/components/admin/NewProduct.js

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card } from '../../components/ui/card';
import {
    Upload,
    DollarSign,
    Tag,
    Box,
    Clock,
    AlertTriangle,
    Save,
    X,
    Loader2,
    ArrowLeft,
    Eye,
    EyeOff
} from 'lucide-react';
import axios from '../../utils/axiosConfig';

const NewProduct = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        priceType: 'perKg',
        price: '',
        price500g: '',
        price1kg: '',
        dozenPrice: '',
        packPrice: '', // Added for perPack price type
        description: '',
        keyHighlights: '',
        category: '',
        availability: 'available',
        stockStatus: 'instock',
        isVisible: true,
        visible500g: true, // Added for weight option visibility
        visible1kg: true   // Added for weight option visibility
    });
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
   
    // Updated categories to include egg
    const categories = [
        "Mutton - Fresh Cuts",
        "Mutton - Premium Cuts",
        "Chicken - Fresh Cuts",
        "Chicken - Special Cuts",
        "Egg -- Fresh Egg"
    ];

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
       
        // Check if we need to update price type based on category
        if (name === 'category' && value === 'Egg -- Fresh Egg') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                priceType: 'perDozen'
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }, []);

    const handleImageChange = useCallback((e) => {
        const files = Array.from(e.target.files);
       
        // Check if total images will exceed 5
        if (files.length + images.length > 5) {
            toast.error('Maximum 5 images allowed in total');
            return;
        }
        // Add new files to existing images
        setImages(prev => [...prev, ...files]);
       
        // Create new previews for the added images
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImagePreviews(prev => [...prev, ...newPreviews]);
    }, [images]);

    const removeImage = useCallback((index) => {
        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(imagePreviews[index]);
       
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    }, [imagePreviews]);

    // Toggle visibility function
    const toggleVisibility = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            isVisible: !prev.isVisible
        }));
    }, []);

    // Updated handleSubmit function with support for perPack price type and weight option visibility
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('=== FRONTEND PRODUCT CREATION SUBMISSION ===');
        console.log('Form data being submitted:');
        console.log('formData:', formData);
        console.log('images:', images);
       
        // Validate images - at least one image is required
        if (images.length === 0) {
            toast.error('Please upload at least one product image');
            return;
        }
       
        // Validate images - maximum 5 images
        if (images.length > 5) {
            toast.error('Maximum 5 product images allowed');
            return;
        }
       
        setLoading(true);
        try {
            console.log('=== CONSTRUCTING FORMDATA FOR CREATION ===');
            const productData = new FormData();
            
            console.log('Adding basic form fields:');
            productData.append('name', formData.name);
            console.log('- name:', formData.name);
            productData.append('priceType', formData.priceType);
            console.log('- priceType:', formData.priceType);
            productData.append('description', formData.description);
            console.log('- description:', formData.description);
            productData.append('keyHighlights', formData.keyHighlights);
            console.log('- keyHighlights:', formData.keyHighlights);
            productData.append('category', formData.category);
            console.log('- category:', formData.category);
            productData.append('availability', formData.availability);
            console.log('- availability:', formData.availability);
            productData.append('stockStatus', formData.stockStatus);
            console.log('- stockStatus:', formData.stockStatus);
            productData.append('isVisible', formData.isVisible);
            console.log('- isVisible:', formData.isVisible);
           
            console.log("Form data summary:", {
                name: formData.name,
                priceType: formData.priceType,
                category: formData.category,
                imageCount: images.length,
                isVisible: formData.isVisible
            });
           
            if (formData.priceType === 'perKg') {
                const price = parseFloat(formData.price);
                if (isNaN(price) || price <= 0) {
                    toast.error('Please enter a valid price per kg');
                    setLoading(false);
                    return;
                }
                productData.append('price', price.toString());
            } else if (formData.priceType === 'perDozen') {
                const dozenPrice = parseFloat(formData.dozenPrice);
                if (isNaN(dozenPrice) || dozenPrice <= 0) {
                    toast.error('Please enter a valid price per dozen');
                    setLoading(false);
                    return;
                }
                productData.append('dozenPrice', dozenPrice.toString());
            } else if (formData.priceType === 'perPack') {
                const packPrice = parseFloat(formData.packPrice);
                if (isNaN(packPrice) || packPrice <= 0) {
                    toast.error('Please enter a valid price per pack');
                    setLoading(false);
                    return;
                }
                productData.append('packPrice', packPrice.toString());
            } else {
                // Fixed weight options
                const price500g = parseFloat(formData.price500g);
                const price1kg = parseFloat(formData.price1kg);
               
                if (isNaN(price500g) || isNaN(price1kg) ||
                    price500g <= 0 || price1kg <= 0) {
                    toast.error('Please enter valid prices for all weight options');
                    setLoading(false);
                    return;
                }
               
                if (price500g >= price1kg) {
                    toast.error('Prices must increase with weight: 500g < 1kg');
                    setLoading(false);
                    return;
                }
               
                productData.append('price500g', price500g.toString());
                productData.append('price1kg', price1kg.toString());
                productData.append('visible500g', formData.visible500g ? 'true' : 'false');
                productData.append('visible1kg', formData.visible1kg ? 'true' : 'false');
            }
           
            // Log image details for debugging
            images.forEach((image, index) => {
                console.log(`Image ${index + 1}:`, {
                    name: image.name,
                    type: image.type,
                    size: `${(image.size / 1024).toFixed(2)}KB`
                });
                productData.append('images', image);
            });
           
            console.log('=== FINAL FORMDATA BEFORE SENDING (CREATION) ===');
            console.log('FormData entries:');
            for (let [key, value] of productData.entries()) {
                console.log(`- ${key}:`, value);
            }
            
            console.log('Sending request to create product...');
            console.log('Request URL:', '/admin/product/new');
            console.log('Request headers:', { 'Content-Type': 'multipart/form-data' });
           
            const { data } = await axios.post(
                '/admin/product/new',
                productData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    withCredentials: true,
                    timeout: 60000 // 60 second timeout for large file uploads
                }
            );
            
            console.log('=== BACKEND RESPONSE (CREATION) ===');
            console.log('Response data:', data);
           
            if (data.success) {
                toast.success('Product created successfully');
                navigate('/admin/products');
            }
        } catch (error) {
            console.error('Product creation error:', error);
           
            // More detailed error handling
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Server response:', error.response.data);
                toast.error(error.response.data.message || 'Error creating product');
            } else if (error.request) {
                // The request was made but no response was received
                console.error('No response received from server');
                toast.error('No response from server. Check your connection.');
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error setting up request:', error.message);
                toast.error(`Error: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back to All Products Button - Right aligned */}
                <div className="flex justify-end mb-4">
                    <button
                        onClick={() => navigate('/admin/products')}
                        className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to All Products</span>
                    </button>
                </div>

                <Card className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                    <div className="flex items-center justify-center mb-4">
                        <h2 className="text-lg font-bold text-[#dc2626] font-['Crimson_Text']">
                            Create New Product
                        </h2>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Product Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Product Name</label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Tag className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    placeholder="Enter product name"
                                    required
                                />
                            </div>
                        </div>
                        
                        {/* Category Selection - Must be before price type to handle dependency */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Category</label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                className="mt-1 block w-full text-xs pl-3 pr-10 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Price Type Selection - Should reflect category constraints */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Price Type</label>
                            <select
                                name="priceType"
                                value={formData.priceType}
                                onChange={handleInputChange}
                                className="mt-1 block w-full text-xs pl-3 pr-10 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                disabled={formData.category === 'Egg -- Fresh Egg'} // Disable if egg category
                            >
                                <option value="perKg" disabled={formData.category === 'Egg -- Fresh Egg'}>Per Kg</option>
                                <option value="fixed" disabled={formData.category === 'Egg -- Fresh Egg'}>Fixed Weight Options</option>
                                <option value="perDozen" disabled={!formData.category.includes('Egg')}>Per Dozen</option>
                                <option value="perPack">Per Pack</option>
                            </select>
                            {formData.category === 'Egg -- Fresh Egg' && (
                                <p className="mt-1 text-xs text-gray-500 italic">
                                    Egg products must use per dozen pricing
                                </p>
                            )}
                        </div>
                        
                        {/* Price Fields based on Price Type */}
                        {formData.priceType === 'perKg' ? (
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Price per Kg (₹)</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                        placeholder="Enter price per kg"
                                        required
                                    />
                                </div>
                            </div>
                        ) : formData.priceType === 'perDozen' ? (
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Price per Dozen (₹)</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="dozenPrice"
                                        value={formData.dozenPrice}
                                        onChange={handleInputChange}
                                        className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                        placeholder="Enter price per dozen"
                                        required
                                    />
                                </div>
                            </div>
                        ) : formData.priceType === 'perPack' ? (
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Price per Pack (₹)</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <DollarSign className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="number"
                                        name="packPrice"
                                        value={formData.packPrice}
                                        onChange={handleInputChange}
                                        className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                        placeholder="Enter price per pack"
                                        required
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* 500g Price */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700">500g Price (₹)</label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="price500g"
                                            value={formData.price500g}
                                            onChange={handleInputChange}
                                            className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                            placeholder="500g price"
                                            required
                                        />
                                    </div>
                                    <div className="mt-1 flex items-center">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                name="visible500g"
                                                checked={formData.visible500g}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    visible500g: e.target.checked
                                                }))}
                                                className="rounded text-[#dc2626] focus:ring-[#dc2626]"
                                            />
                                            <span className="ml-2 text-xs text-gray-700">Visible to customers</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {/* 1kg Price */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700">1kg Price (₹)</label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            name="price1kg"
                                            value={formData.price1kg}
                                            onChange={handleInputChange}
                                            className="block w-full text-xs pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                            placeholder="1kg price"
                                            required
                                        />
                                    </div>
                                    <div className="mt-1 flex items-center">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                name="visible1kg"
                                                checked={formData.visible1kg}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    visible1kg: e.target.checked
                                                }))}
                                                className="rounded text-[#dc2626] focus:ring-[#dc2626]"
                                            />
                                            <span className="ml-2 text-xs text-gray-700">Visible to customers</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Description */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Description</label>
                            <div className="mt-1">
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="block w-full text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    placeholder="Enter product description"
                                    required
                                />
                            </div>
                        </div>
                       
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Key Highlights</label>
                            <div className="mt-1">
                                <textarea
                                    name="keyHighlights"
                                    value={formData.keyHighlights}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="block w-full text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    placeholder="Enter key highlights"
                                    required
                                />
                            </div>
                        </div>
                       
                        {/* Images */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">
                                Product Images (Max 5) - Currently {imagePreviews.length}/5
                            </label>
                            <div className="mt-1 flex justify-center px-4 pt-3 pb-4 border-2 border-dashed border-gray-300 rounded-lg">
                                <div className="space-y-1 text-center">
                                    <Upload className="mx-auto h-8 w-8 text-gray-400" />
                                    <div className="flex text-xs text-gray-600 justify-center">
                                        <label className={`relative cursor-pointer bg-white rounded-md font-medium text-[#dc2626] hover:text-[#dc2626]/80 ${imagePreviews.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <span>Upload images</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="sr-only"
                                                disabled={imagePreviews.length >= 5}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        PNG, JPG, GIF up to 2MB each
                                    </p>
                                </div>
                            </div>
                            
                            {/* Image Preview Grid */}
                            {imagePreviews.length > 0 && (
                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                    {imagePreviews.map((preview, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={preview}
                                                alt={`Preview ${index + 1}`}
                                                className="h-20 w-full object-cover rounded-lg shadow-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-opacity opacity-90 hover:opacity-100"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Availability */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Availability</label>
                            <select
                                name="availability"
                                value={formData.availability}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        availability: newValue,
                                        // Reset stockStatus to instock if availability is set to unavailable
                                        stockStatus: newValue === 'unavailable' ? 'instock' : prev.stockStatus
                                    }));
                                }}
                                className="mt-1 block w-full text-xs pl-3 pr-10 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                required
                            >
                                <option value="available">Available</option>
                                <option value="unavailable">Unavailable</option>
                            </select>
                        </div>
                        
                        {/* Stock Status - Only show if available */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">
                                Stock Status
                                {formData.availability === 'unavailable' && (
                                    <span className="ml-2 text-xs text-red-500">
                                        (Disabled: Product is set as unavailable)
                                    </span>
                                )}
                            </label>
                            <select
                                name="stockStatus"
                                value={formData.stockStatus}
                                onChange={handleInputChange}
                                className={`mt-1 block w-full text-xs pl-3 pr-10 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center ${
                                    formData.availability === 'unavailable' ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                                }`}
                                disabled={formData.availability === 'unavailable'}
                                required
                            >
                                <option value="instock">In Stock</option>
                                <option value="lowstock">Low Stock</option>
                            </select>
                            {formData.availability === 'unavailable' && (
                                <p className="mt-1 text-xs text-gray-500 text-center">
                                    Stock status is automatically set when product is unavailable.
                                </p>
                            )}
                        </div>
                       
                        {/* Visibility Toggle */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Visibility</label>
                            <div className="mt-1 flex items-center">
                                <button
                                    type="button"
                                    onClick={toggleVisibility}
                                    className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm border ${
                                        formData.isVisible
                                            ? 'border-green-500 text-green-600 bg-green-50'
                                            : 'border-gray-400 text-gray-600 bg-gray-50'
                                    }`}
                                >
                                    {formData.isVisible ? (
                                        <>
                                            <Eye className="h-4 w-4" />
                                            <span>Visible</span>
                                        </>
                                    ) : (
                                        <>
                                            <EyeOff className="h-4 w-4" />
                                            <span>Hidden</span>
                                        </>
                                    )}
                                </button>
                                <p className="ml-3 text-xs text-gray-500">
                                    {formData.isVisible
                                        ? 'Product will be visible to customers'
                                        : 'Product will be hidden from customers'}
                                </p>
                            </div>
                        </div>
                        
                        {/* Submit Button */}
                        <div className="flex justify-center pt-4">
                            <button
                                type="submit"
                                disabled={loading || images.length === 0}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-1.5" />
                                        Create Product
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Card>
            </div>
           
            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 640px) {
                    .px-4 {
                        padding-left: 0.75rem;
                        padding-right: 0.75rem;
                    }
                   
                    .py-1\.5 {
                        padding-top: 0.375rem;
                        padding-bottom: 0.375rem;
                    }
                   
                    .text-xs {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                   
                    .grid-cols-2 {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                   
                    .h-20 {
                        height: 5rem;
                    }
                   
                    .p-4 {
                        padding: 0.75rem;
                    }
                    
                    .gap-3 {
                        gap: 0.5rem;
                    }
                   
                    .text-center {
                        text-align: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default NewProduct;